import { ConstantsUtil } from '../utils/ConstantsUtil.js';
import { CoreHelperUtil } from '../utils/CoreHelperUtil.js';
import { FetchUtil } from '../utils/FetchUtil.js';
import { OptionsController } from './OptionsController.js';
import { proxy } from 'valtio/vanilla';
import { AccountController } from './AccountController.js';
import { ChainController } from './ChainController.js';
const DEFAULT_OPTIONS = {
    purchaseCurrencies: [
        {
            id: '2b92315d-eab7-5bef-84fa-089a131333f5',
            name: 'USD Coin',
            symbol: 'USDC',
            networks: [
                {
                    name: 'ethereum-mainnet',
                    display_name: 'Ethereum',
                    chain_id: '1',
                    contract_address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48'
                },
                {
                    name: 'polygon-mainnet',
                    display_name: 'Polygon',
                    chain_id: '137',
                    contract_address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'
                }
            ]
        },
        {
            id: '2b92315d-eab7-5bef-84fa-089a131333f5',
            name: 'Ether',
            symbol: 'ETH',
            networks: [
                {
                    name: 'ethereum-mainnet',
                    display_name: 'Ethereum',
                    chain_id: '1',
                    contract_address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48'
                },
                {
                    name: 'polygon-mainnet',
                    display_name: 'Polygon',
                    chain_id: '137',
                    contract_address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'
                }
            ]
        }
    ],
    paymentCurrencies: [
        {
            id: 'USD',
            payment_method_limits: [
                {
                    id: 'card',
                    min: '10.00',
                    max: '7500.00'
                },
                {
                    id: 'ach_bank_account',
                    min: '10.00',
                    max: '25000.00'
                }
            ]
        },
        {
            id: 'EUR',
            payment_method_limits: [
                {
                    id: 'card',
                    min: '10.00',
                    max: '7500.00'
                },
                {
                    id: 'ach_bank_account',
                    min: '10.00',
                    max: '25000.00'
                }
            ]
        }
    ]
};
// -- Helpers ------------------------------------------- //
const baseUrl = CoreHelperUtil.getBlockchainApiUrl();
// -- State --------------------------------------------- //
const state = proxy({
    clientId: null,
    api: new FetchUtil({ baseUrl, clientId: null })
});
// -- Controller ---------------------------------------- //
export const BlockchainApiController = {
    state,
    fetchIdentity({ address }) {
        return state.api.get({
            path: `/v1/identity/${address}`,
            params: {
                projectId: OptionsController.state.projectId,
                sender: ChainController.state.activeCaipAddress
                    ? CoreHelperUtil.getPlainAddress(ChainController.state.activeCaipAddress)
                    : undefined
            }
        });
    },
    fetchTransactions({ account, projectId, cursor, onramp, signal, cache, chainId }) {
        return state.api.get({
            path: `/v1/account/${account}/history`,
            params: {
                projectId,
                cursor,
                onramp,
                chainId
            },
            signal,
            cache
        });
    },
    fetchSwapQuote({ projectId, amount, userAddress, from, to, gasPrice }) {
        return state.api.get({
            path: `/v1/convert/quotes`,
            headers: {
                'Content-Type': 'application/json'
            },
            params: {
                projectId,
                amount,
                userAddress,
                from,
                to,
                gasPrice
            }
        });
    },
    fetchSwapTokens({ projectId, chainId }) {
        return state.api.get({
            path: `/v1/convert/tokens`,
            params: {
                projectId,
                chainId
            }
        });
    },
    fetchTokenPrice({ projectId, addresses }) {
        return state.api.post({
            path: '/v1/fungible/price',
            body: {
                projectId,
                currency: 'usd',
                addresses
            },
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },
    fetchSwapAllowance({ projectId, tokenAddress, userAddress }) {
        const { sdkType, sdkVersion } = OptionsController.state;
        return state.api.get({
            path: `/v1/convert/allowance`,
            params: {
                projectId,
                tokenAddress,
                userAddress
            },
            headers: {
                'Content-Type': 'application/json',
                'x-sdk-type': sdkType,
                'x-sdk-version': sdkVersion || 'html-wagmi-4.2.2'
            }
        });
    },
    fetchGasPrice({ projectId, chainId }) {
        const { sdkType, sdkVersion } = OptionsController.state;
        return state.api.get({
            path: `/v1/convert/gas-price`,
            headers: {
                'Content-Type': 'application/json',
                'x-sdk-type': sdkType,
                'x-sdk-version': sdkVersion || 'html-wagmi-4.2.2'
            },
            params: {
                projectId,
                chainId
            }
        });
    },
    generateSwapCalldata({ amount, from, projectId, to, userAddress }) {
        return state.api.post({
            path: '/v1/convert/build-transaction',
            headers: {
                'Content-Type': 'application/json'
            },
            body: {
                amount,
                eip155: {
                    slippage: ConstantsUtil.CONVERT_SLIPPAGE_TOLERANCE
                },
                from,
                projectId,
                to,
                userAddress
            }
        });
    },
    generateApproveCalldata({ from, projectId, to, userAddress }) {
        const { sdkType, sdkVersion } = OptionsController.state;
        return state.api.get({
            path: `/v1/convert/build-approve`,
            headers: {
                'Content-Type': 'application/json',
                'x-sdk-type': sdkType,
                'x-sdk-version': sdkVersion || 'html-wagmi-4.2.2'
            },
            params: {
                projectId,
                userAddress,
                from,
                to
            }
        });
    },
    async getBalance(address, chainId, forceUpdate) {
        const { sdkType, sdkVersion } = OptionsController.state;
        return state.api.get({
            path: `/v1/account/${address}/balance`,
            headers: {
                'x-sdk-type': sdkType,
                'x-sdk-version': sdkVersion || 'html-wagmi-4.2.2'
            },
            params: {
                currency: 'usd',
                projectId: OptionsController.state.projectId,
                chainId,
                forceUpdate
            }
        });
    },
    async lookupEnsName(name) {
        return state.api.get({
            path: `/v1/profile/account/${name}`,
            params: {
                projectId: OptionsController.state.projectId,
                apiVersion: '2'
            }
        });
    },
    async reverseLookupEnsName({ address }) {
        return state.api.get({
            path: `/v1/profile/reverse/${address}`,
            params: {
                sender: AccountController.state.address,
                projectId: OptionsController.state.projectId,
                apiVersion: '2'
            }
        });
    },
    async getEnsNameSuggestions(name) {
        return state.api.get({
            path: `/v1/profile/suggestions/${name}`,
            params: {
                projectId: OptionsController.state.projectId,
                zone: 'reown.id'
            }
        });
    },
    async registerEnsName({ coinType, address, message, signature }) {
        return state.api.post({
            path: `/v1/profile/account`,
            body: { coin_type: coinType, address, message, signature },
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },
    async generateOnRampURL({ destinationWallets, partnerUserId, defaultNetwork, purchaseAmount, paymentAmount }) {
        const response = await state.api.post({
            path: `/v1/generators/onrampurl`,
            params: {
                projectId: OptionsController.state.projectId
            },
            body: {
                destinationWallets,
                defaultNetwork,
                partnerUserId,
                defaultExperience: 'buy',
                presetCryptoAmount: purchaseAmount,
                presetFiatAmount: paymentAmount
            }
        });
        return response.url;
    },
    async getOnrampOptions() {
        try {
            const response = await state.api.get({
                path: `/v1/onramp/options`,
                params: {
                    projectId: OptionsController.state.projectId
                }
            });
            return response;
        }
        catch (e) {
            return DEFAULT_OPTIONS;
        }
    },
    async getOnrampQuote({ purchaseCurrency, paymentCurrency, amount, network }) {
        try {
            const response = await state.api.post({
                path: `/v1/onramp/quote`,
                params: {
                    projectId: OptionsController.state.projectId
                },
                body: {
                    purchaseCurrency,
                    paymentCurrency,
                    amount,
                    network
                }
            });
            return response;
        }
        catch (e) {
            // Mocking response as 1:1 until endpoint is ready
            return {
                coinbaseFee: { amount, currency: paymentCurrency.id },
                networkFee: { amount, currency: paymentCurrency.id },
                paymentSubtotal: { amount, currency: paymentCurrency.id },
                paymentTotal: { amount, currency: paymentCurrency.id },
                purchaseAmount: { amount, currency: paymentCurrency.id },
                quoteId: 'mocked-quote-id'
            };
        }
    },
    getSmartSessions(caipAddress) {
        return state.api.get({
            path: `/v1/sessions/${caipAddress}`,
            params: {
                projectId: OptionsController.state.projectId
            }
        });
    },
    revokeSmartSession(address, pci, signature) {
        return state.api.post({
            path: `/v1/sessions/${address}/revoke`,
            params: {
                projectId: OptionsController.state.projectId
            },
            body: {
                pci,
                signature
            }
        });
    },
    setClientId(clientId) {
        state.clientId = clientId;
        state.api = new FetchUtil({ baseUrl, clientId });
    }
};
//# sourceMappingURL=BlockchainApiController.js.map